import Vue from 'vue';
import { uploadFilesToFolder, UPLOAD_DOCUMENTS } from '@/utils/attachmentsAPI';
import documentsService from '@Task/service/documents.service';
import { addVersionOutcome, addVersionMessage } from '@/features/Documents/utils/documentVersions';
import { EventBus, DOCUMENT_EVENTS } from '@/event-bus';
import { isAbortError } from '@/utils/isAbortError';
import { log as debugLog, error as debugError } from '@/utils/debugLogger';

/**
 * Background queue for document uploads, shown by DocumentUploadTray. Two kinds of batch:
 *  - 'documents': new documents from the Add Document dialog (ENQUEUE)
 *  - 'version':   a new version of an existing document from the workspace (ENQUEUE_VERSION)
 *
 * The dialogs hand their files here and close, so the user keeps working while the files
 * upload. Batches run one at a time: a documents batch may stop to ask about taken names
 * (DocumentNameConflictsDialog), and two of those prompts must not interleave.
 *
 * File objects, upload arguments and AbortControllers live in `runtime`, outside the store:
 * they are not serialisable state and Vue must not observe them.
 */

/** @type {Map<string, { files: File[], args: Object, controller: AbortController|null }>} */
const runtime = new Map();

/** Batches that are still working (or waiting to). */
const ACTIVE_STATUSES = ['queued', 'checking', 'uploading', 'saving'];
/** Batches the user can stop: the file transfer can be aborted, a created document cannot. */
const CANCELLABLE_STATUSES = ['queued', 'uploading'];

let processing = false;

function newBatchId() {
  return `upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function toFileEntry(file, index) {
  return { index, name: file.name, size: file.size, status: 'queued', progress: 0 };
}

function errorMessage(err) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.length < 300) return data;
  return data?.message || data?.title || err?.message || 'Upload failed';
}

/**
 * Spreads the transferred bytes over the files in the order they were added to the FormData,
 * which gives a per-file progress from the single multipart request.
 */
function fileProgress(files, loaded) {
  let remaining = loaded;
  return files.map((file) => {
    if (file.status !== 'uploading' && file.status !== 'done') return file.progress;
    const size = Math.max(file.size, 1);
    const sent = Math.min(Math.max(remaining, 0), size);
    remaining -= size;
    return Math.round((sent / size) * 100);
  });
}

function newBatch(id, kind, fileList, extra = {}) {
  return {
    id,
    kind,
    status: 'queued',
    scopeLabel: '',
    files: fileList.map(toFileEntry),
    totalBytes: fileList.reduce((sum, f) => sum + f.size, 0),
    loadedBytes: 0,
    bytesPerSecond: 0,
    queuedAt: Date.now(),
    startedAt: null,
    finishedAt: null,
    error: null,
    savedCount: 0,
    ...extra,
  };
}

/**
 * Uploads the file of a 'version' batch and adds it as a version of its document. Reports the
 * outcome (new current version / pinned version stays / identical file) like the workspace did,
 * and announces the change so an open workspace and the documents list refresh.
 */
async function uploadVersion(entry, { signal, onPhase, onProgress }) {
  const [file] = entry.files;
  const { documentId, revisionNumber, transferLinks, allowDuplicateContent } = entry.args;

  onPhase('uploading', { fileIndexes: [0] });
  const [uploaded] = await UPLOAD_DOCUMENTS([file], {
    signal,
    throwOnError: true,
    onUploadProgress: (event) => onProgress(event.loaded, event.total),
  });
  if (!uploaded?.storageReference) throw new Error('The server did not return the uploaded file');

  // Not cancellable from here: once stored, the version is added even if the user navigates away.
  onPhase('saving');
  const res = await documentsService.addRevision(
    {
      documentId,
      fileName: uploaded.fileName,
      storageReference: uploaded.storageReference,
      size: uploaded.size,
      contentHash: uploaded.contentHash,
      contentType: uploaded.contentType,
      revisionNumber,
      // The user's answer to "transfer the links to the new version?"
      transferLinks: !!transferLinks,
      // The user was told the file is identical to the current version and chose to keep it.
      allowDuplicateContent: !!allowDuplicateContent,
    },
    { critical: true },
  );

  const outcome = addVersionOutcome(res.headers);
  const { type, text } = addVersionMessage(outcome, res.data);
  Vue.prototype.$dialog?.notify?.[type]?.(text, { position: 'top-right', timeout: 6000 });
  if (!outcome.duplicate) EventBus.$emit(DOCUMENT_EVENTS.DOCUMENT_CHANGED, res.data);
  return [res.data];
}

export const DOCUMENT_UPLOADS = {
  namespaced: true,
  state: {
    /** Newest first. */
    batches: [],
    isMinimized: false,
  },
  getters: {
    GET_BATCHES: (state) => state.batches,
    GET_IS_MINIMIZED: (state) => state.isMinimized,
    GET_ACTIVE_BATCHES: (state) => state.batches.filter((b) => ACTIVE_STATUSES.includes(b.status)),
    HAS_ACTIVE_UPLOADS: (state) => state.batches.some((b) => ACTIVE_STATUSES.includes(b.status)),
    HAS_FINISHED_BATCHES: (state) => state.batches.some((b) => !ACTIVE_STATUSES.includes(b.status)),
    IS_CANCELLABLE: () => (batch) => CANCELLABLE_STATUSES.includes(batch.status),
  },
  mutations: {
    ADD_BATCH(state, batch) {
      state.batches.unshift(batch);
    },
    UPDATE_BATCH(state, { id, updates }) {
      const batch = state.batches.find((b) => b.id === id);
      if (batch) Object.assign(batch, updates);
    },
    SET_FILES(state, { id, files }) {
      const batch = state.batches.find((b) => b.id === id);
      if (batch) batch.files = files;
    },
    REMOVE_BATCH(state, id) {
      state.batches = state.batches.filter((b) => b.id !== id);
    },
    SET_MINIMIZED(state, value) {
      state.isMinimized = value;
    },
  },
  actions: {
    /**
     * Queue files for upload. Everything that decides where the documents go (facility, scope,
     * type, tags) is captured now, so navigating elsewhere during the upload cannot change it.
     *
     * @param {Object} payload
     * @param {File[]} payload.files
     * @param {string} payload.folder
     * @param {string|null} [payload.documentTypeId]
     * @param {{ scopeType: string, facilityGroupId: string|null }} [payload.scopeOptions]
     * @param {Object|null} [payload.predefined] - GET_PREDEFINED_DOCUMENT_INFO at the time of queueing
     * @param {string} [payload.scopeLabel] - Where the files go, for display ("Facility", a group name)
     * @returns {string} batch id
     */
    ENQUEUE({ commit, dispatch, rootState }, { files, folder, documentTypeId = null, scopeOptions = null, predefined = null, scopeLabel = '' }) {
      const id = newBatchId();
      const fileList = Array.from(files || []);
      if (!fileList.length) return null;

      runtime.set(id, {
        files: fileList,
        args: {
          folder,
          documentTypeId,
          scopeOptions,
          predefined: predefined ? structuredClone(predefined) : null,
          assetId: rootState.assetId,
        },
        controller: null,
      });

      commit('ADD_BATCH', newBatch(id, 'documents', fileList, { scopeLabel }));
      commit('SET_MINIMIZED', false);

      dispatch('PROCESS_QUEUE');
      return id;
    },

    /**
     * Queue a file as a new version of an existing document.
     *
     * @param {Object} payload
     * @param {string} payload.documentId
     * @param {File} payload.file
     * @param {string} [payload.revisionNumber] - Engineering revision label (B, 02, ...)
     * @param {string} [payload.documentLabel] - The document's number or title, for display
     * @param {boolean} [payload.transferLinks] - Copy the links on the current version onto the new one
     * @param {boolean} [payload.allowDuplicateContent] - Add the file even if identical to the current version
     * @returns {string} batch id
     */
    ENQUEUE_VERSION(
      { commit, dispatch },
      { documentId, file, revisionNumber = '', documentLabel = '', transferLinks = false, allowDuplicateContent = false },
    ) {
      if (!documentId || !file) return null;
      const id = newBatchId();

      runtime.set(id, {
        files: [file],
        args: {
          documentId,
          revisionNumber: (revisionNumber || '').trim(),
          transferLinks: !!transferLinks,
          allowDuplicateContent: !!allowDuplicateContent,
        },
        controller: null,
      });

      commit('ADD_BATCH', newBatch(id, 'version', [file], { documentId, documentLabel, revisionNumber }));
      commit('SET_MINIMIZED', false);

      dispatch('PROCESS_QUEUE');
      return id;
    },

    async PROCESS_QUEUE({ state, dispatch }) {
      if (processing) return;
      processing = true;
      try {
        let next;
        // Oldest queued batch first (batches are stored newest first).
        while ((next = [...state.batches].reverse().find((b) => b.status === 'queued'))) {
          await dispatch('RUN_BATCH', next.id);
        }
      } finally {
        processing = false;
      }
    },

    async RUN_BATCH({ state, commit }, id) {
      const entry = runtime.get(id);
      const batch = state.batches.find((b) => b.id === id);
      if (!entry || !batch) return;

      const controller = new AbortController();
      entry.controller = controller;
      const { folder, documentTypeId, scopeOptions, predefined, assetId } = entry.args;

      commit('UPDATE_BATCH', {
        id,
        updates: { status: 'checking', startedAt: Date.now(), loadedBytes: 0, bytesPerSecond: 0, error: null },
      });

      let uploadStartedAt = 0;
      let lastPercent = -1;
      let reachedUpload = false;

      const onPhase = (phase, detail) => {
        if (phase === 'uploading') {
          reachedUpload = true;
          uploadStartedAt = Date.now();
          const sent = new Set(detail?.fileIndexes ?? []);
          const files = batch.files.map((f) => ({ ...f, status: sent.has(f.index) ? 'uploading' : 'skipped' }));
          const totalBytes = files.filter((f) => f.status === 'uploading').reduce((sum, f) => sum + f.size, 0);
          commit('SET_FILES', { id, files });
          commit('UPDATE_BATCH', { id, updates: { status: 'uploading', totalBytes } });
        } else if (phase === 'saving') {
          commit('UPDATE_BATCH', { id, updates: { status: 'saving', loadedBytes: batch.totalBytes } });
        }
      };

      const onProgress = (loaded, total) => {
        const totalBytes = total || batch.totalBytes;
        const percent = totalBytes ? Math.floor((loaded / totalBytes) * 100) : 0;
        // Progress events fire many times a second; only a whole-percent change is worth a render.
        if (percent === lastPercent) return;
        lastPercent = percent;
        const elapsed = (Date.now() - uploadStartedAt) / 1000;
        const uploading = batch.files.filter((f) => f.status === 'uploading' || f.status === 'done');
        const progress = fileProgress(uploading, loaded);
        const files = batch.files.map((f) => {
          const i = uploading.indexOf(f);
          return i < 0 ? f : { ...f, progress: progress[i] };
        });
        commit('SET_FILES', { id, files });
        commit('UPDATE_BATCH', {
          id,
          updates: { loadedBytes: Math.min(loaded, batch.totalBytes || loaded), bytesPerSecond: elapsed > 0.5 ? loaded / elapsed : 0 },
        });
      };

      try {
        const saved =
          batch.kind === 'version'
            ? await uploadVersion(entry, { signal: controller.signal, onPhase, onProgress })
            : await uploadFilesToFolder(entry.files, folder, documentTypeId, scopeOptions, {
                assetId,
                predefined,
                signal: controller.signal,
                onPhase,
                onProgress,
                openReview: false,
                throwOnError: true,
              });

        if (!reachedUpload) {
          // The user cancelled the name-conflict prompt or skipped every file.
          commit('SET_FILES', { id, files: batch.files.map((f) => ({ ...f, status: 'skipped' })) });
          commit('UPDATE_BATCH', { id, updates: { status: 'cancelled', finishedAt: Date.now() } });
          return;
        }
        if (!saved?.length) {
          throw new Error('The server did not return the uploaded documents');
        }

        commit('SET_FILES', {
          id,
          files: batch.files.map((f) => (f.status === 'uploading' ? { ...f, status: 'done', progress: 100 } : f)),
        });
        commit('UPDATE_BATCH', {
          id,
          updates: {
            status: 'completed',
            finishedAt: Date.now(),
            savedCount: saved.length,
          },
        });
        runtime.get(id).files = []; // Done: let the browser release the file data.
        debugLog('[DocumentUploads]', 'Batch completed:', id, saved.length);
      } catch (err) {
        const cancelled = isAbortError(err) || controller.signal.aborted;
        if (!cancelled) debugError('[DocumentUploads]', 'Batch failed:', id, err);
        const fileStatus = cancelled ? 'cancelled' : 'failed';
        commit('SET_FILES', {
          id,
          files: batch.files.map((f) => (f.status === 'skipped' || f.status === 'done' ? f : { ...f, status: fileStatus })),
        });
        commit('UPDATE_BATCH', {
          id,
          updates: {
            status: cancelled ? 'cancelled' : 'failed',
            finishedAt: Date.now(),
            error: cancelled ? null : errorMessage(err),
          },
        });
        if (!cancelled) {
          Vue.prototype.$dialog?.notify?.error?.('A document upload failed. Open the uploads panel to retry.', {
            position: 'top-right',
            timeout: 6000,
          });
        }
      } finally {
        const current = runtime.get(id);
        if (current) current.controller = null;
      }
    },

    CANCEL({ state, commit }, id) {
      const batch = state.batches.find((b) => b.id === id);
      if (!batch || !CANCELLABLE_STATUSES.includes(batch.status)) return;

      if (batch.status === 'queued') {
        commit('SET_FILES', { id, files: batch.files.map((f) => ({ ...f, status: 'cancelled' })) });
        commit('UPDATE_BATCH', { id, updates: { status: 'cancelled', finishedAt: Date.now() } });
        return;
      }
      // RUN_BATCH sees the abort and marks the batch cancelled.
      runtime.get(id)?.controller?.abort();
    },

    RETRY({ state, commit, dispatch }, id) {
      const batch = state.batches.find((b) => b.id === id);
      const entry = runtime.get(id);
      if (!batch || !entry?.files?.length || !['failed', 'cancelled'].includes(batch.status)) return;

      commit('SET_FILES', { id, files: entry.files.map(toFileEntry) });
      commit('UPDATE_BATCH', {
        id,
        updates: {
          status: 'queued',
          totalBytes: entry.files.reduce((sum, f) => sum + f.size, 0),
          loadedBytes: 0,
          bytesPerSecond: 0,
          error: null,
          startedAt: null,
          finishedAt: null,
        },
      });
      dispatch('PROCESS_QUEUE');
    },

    REMOVE({ state, commit }, id) {
      const batch = state.batches.find((b) => b.id === id);
      if (!batch || ACTIVE_STATUSES.includes(batch.status)) return;
      runtime.delete(id);
      commit('REMOVE_BATCH', id);
    },

    CLEAR_FINISHED({ state, dispatch }) {
      state.batches
        .filter((b) => !ACTIVE_STATUSES.includes(b.status))
        .forEach((b) => dispatch('REMOVE', b.id));
    },

    TOGGLE_MINIMIZE({ state, commit }) {
      commit('SET_MINIMIZED', !state.isMinimized);
    },
  },
};
